import { ViewList } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  Grid2,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { QuickComment, quickComments } from "constants/quickComments";
import { useFormik, useFormikContext } from "formik";
import { useCallback, useMemo, useState } from "react";
import { Inventory } from "types/inventory";

interface SelectedItem {
  item: string;
  group: string;
}

const QuickCommentsMenuListItem = ({
  item,
  groupName,
  selectedItemsSet,
  handleCheckboxChange,
}: {
  item: QuickComment;
  groupName: string;
  selectedItemsSet: Set<string>;
  handleCheckboxChange: (item: string, groupName: string, checked: boolean) => void;
}) => {
  return (
    <ListItem>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedItemsSet.has(item.label)}
            onChange={(e) => handleCheckboxChange(item.label, groupName, e.target.checked)}
          />
        }
        label={item.label}
        sx={{
          width: "100%",
          transition: "0.2s",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      />
    </ListItem>
  );
};

const QuickCommentsMenu = ({
  open,
  handleClose,
  handleSubmit,
}: {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (values: { selectedItems: SelectedItem[] }) => void;
}) => {
  const form = useFormik({
    initialValues: { selectedItems: [] as SelectedItem[] },
    onSubmit: (values) => {
      handleSubmit(values);
      handleClose();
    },
  });

  const selectedItemsSet = useMemo(() => {
    return new Set(form.values.selectedItems.map((selectedItem) => selectedItem.item));
  }, [form.values.selectedItems]);

  const handleCheckboxChange = useCallback(
    (item: string, group: string, checked: boolean) => {
      const currentItems = form.values.selectedItems;
      if (checked) {
        form.setFieldValue("selectedItems", [...currentItems, { item, group }]);
      } else {
        form.setFieldValue(
          "selectedItems",
          currentItems.filter((i) => i.item !== item),
        );
      }
    },
    [form],
  );

  const menuItems = useMemo(() => {
    return quickComments.map((group) => (
      <List key={group.name} dense>
        <ListItemText primary={group.name} slotProps={{ primary: { sx: { fontWeight: "bold" } } }} />
        {group.items.map((item, index) => (
          <QuickCommentsMenuListItem
            key={index}
            item={item}
            groupName={group.name}
            selectedItemsSet={selectedItemsSet}
            handleCheckboxChange={handleCheckboxChange}
          />
        ))}
        <ListItem divider />
      </List>
    ));
  }, [selectedItemsSet, handleCheckboxChange]);

  const getFormattedPreview = useMemo(() => {
    const groupedItems: { [key: string]: string[] } = {};

    form.values.selectedItems.forEach(({ item, group }) => {
      if (!groupedItems[group]) {
        groupedItems[group] = [];
      }
      groupedItems[group].push(item);
    });

    return Object.entries(groupedItems)
      .map(([groupName, items]) => `${groupName}: ${items.join(", ")}`)
      .join("\n");
  }, [form.values.selectedItems]);

  return (
    <Drawer open={open} onClose={handleClose} anchor="right">
      <Box padding={2}>
        <Typography color="text.primary" variant="h6">
          Defect Comments
        </Typography>
        <Typography color="text.secondary" variant="caption">
          Select comments to apply
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: "100%",
          overflow: "auto",
          paddingLeft: 2,
        }}
      >
        <form onSubmit={form.handleSubmit}>{menuItems}</form>
      </Box>
      <Box padding={2}>
        <Typography color="text.secondary" variant="caption">
          Preview
        </Typography>
        <Typography component={Paper} padding={2} sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem" }}>
          {getFormattedPreview}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={!form.values.selectedItems.length} onClick={() => form.submitForm()}>
            Apply
          </Button>
          <Button onClick={() => form.resetForm()}>Clear</Button>
          <Button onClick={handleClose}>Close</Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

function QuickCommentChips({ handleApply }: { handleApply: (value: string) => void }) {
  const {
    values: { inventory },
  } = useFormikContext<{ inventory: Inventory }>();
  const suggestable = useMemo(() => {
    const flags = [];
    if (inventory.condition == "D") flags.push("dGrade");
    if (inventory.securityLevel == "BIOS locked") flags.push("biosLocked");
    if (inventory.screenFunctionality == "Broken") flags.push("lcdBroken");
    if (inventory.cameraFunctionality == "Broken") flags.push("cameraBroken");
    if (inventory.keyboardFunctionality == "Broken") flags.push("keyboardBroken");
    if (inventory.trackpadFunctionality == "Broken") flags.push("trackpadBroken");
    if (inventory.batteryFunctionality == "Broken" || inventory.batteryHealth == 0) flags.push("batteryBroken");
    if (inventory.microphoneFunctionality == "Broken") flags.push("microphoneBroken");
    if (inventory.speakerFunctionality == "Broken") flags.push("speakerBroken");
    if (inventory.portsFunctionality == "Broken") flags.push("portsBroken");
    if (inventory.powerFunctionality == "Broken") flags.push("powerBroken");
    if (inventory.logicFunctionality == "Broken") flags.push("logicBroken");

    return flags;
  }, [inventory]);

  const chips = useMemo(() => {
    const items = quickComments.map((group) => group.items).flat();
    return items
      .filter((item) => item.suggestWhen.some((suggest) => suggestable.includes(suggest)))
      .slice(0, 5)
      .map((item) => {
        return (
          <Chip
            clickable
            tabIndex={-1}
            label={item.shorthandLabel}
            size="small"
            onClick={() => handleApply(item.label)}
          />
        );
      });
  }, [suggestable, handleApply]);

  return (
    <Stack
      direction="row"
      spacing={1}
      paddingY={1}
      minWidth={0}
      maxWidth="100%"
      flexWrap="nowrap"
      sx={{ overflowX: "scroll" }}
    >
      {chips}
    </Stack>
  );
}

export function QuickCommentsToolbar({ onApply }: { onApply: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSubmit = (formValues: { selectedItems: SelectedItem[] }) => {
    const groupedItems: { [key: string]: string[] } = {};

    formValues.selectedItems.forEach(({ item, group }) => {
      if (!groupedItems[group]) {
        groupedItems[group] = [];
      }
      groupedItems[group].push(item);
    });

    const formattedString = Object.entries(groupedItems)
      .map(([groupName, items]) => `${groupName}: ${items.join(", ")}`)
      .join("\n");

    onApply(formattedString);
    handleClose();
  };

  return (
    <Grid2 container spacing={1} alignItems="center">
      <Grid2 size="grow" paddingY={1} maxWidth="100%">
        <QuickCommentChips handleApply={(value) => onApply(value)} />
      </Grid2>
      <Grid2 size="auto">
        <Button onClick={handleClickOpen} size="small" startIcon={<ViewList />} tabIndex={-1}>
          Defects
        </Button>
      </Grid2>
      <QuickCommentsMenu open={open} handleClose={handleClose} handleSubmit={handleSubmit} />
    </Grid2>
  );
}
